import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Archive, ExternalLink, Link2, Mail, MessageSquare, Pencil, Star, Trash2, Undo2 } from "lucide-react";
import api from "@/lib/api";
import useCrmConfig from "@/hooks/useCrmConfig";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loading } from "@/components/common/Loading";
import ContactFormDialog from "@/components/crm/ContactFormDialog";
import ContactNotes from "@/components/crm/ContactNotes";
import ContactSubrecords from "@/components/crm/ContactSubrecords";
import ContactTags from "@/components/crm/ContactTags";
import ContactTimeline from "@/components/crm/ContactTimeline";
import LinkPersonDialog, { UnlinkButton } from "@/components/crm/LinkPersonDialog";

const pretty = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const Field = ({ label, children, testid }) => (
  <div>
    <dt className="text-xs font-[600] uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="mt-0.5 text-sm text-foreground/90" data-testid={testid}>{children || "—"}</dd>
  </div>
);

export default function ContactDetail() {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { visible, loading: configLoading, enums } = useCrmConfig();
  const [contact, setContact] = useState(null);
  const [missing, setMissing] = useState(false);
  const [edit, setEdit] = useState(false);
  const [linking, setLinking] = useState(false);
  const [actions, setActions] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/crm/contacts/${contactId}`);
      setContact(data);
      api.get(`/crm/contacts/${contactId}/actions`)
        .then((r) => setActions(r.data)).catch(() => setActions(null));
    } catch { setMissing(true); }
  }, [contactId]);

  useEffect(() => { if (visible) load(); }, [visible, load]);

  const patch = async (updates) => {
    try {
      const { data } = await api.patch(`/crm/contacts/${contactId}`, updates);
      setContact(data);
    } catch { toast.error("Couldn't update this contact"); }
  };

  const remove = async () => {
    try {
      await api.delete(`/crm/contacts/${contactId}`);
      toast.success("Contact deleted — recoverable for 30 days");
      navigate("/dashboard/contacts");
    } catch { toast.error("Couldn't delete this contact"); }
  };

  if (configLoading) return <Loading />;
  if (!visible || missing) {
    return (
      <div className="py-20 text-center" data-testid="contact-not-found">
        <h1 className="text-2xl font-[680]">Page not found</h1>
        <p className="mt-2 text-muted-foreground">This contact isn't available on your account.</p>
        <Button className="mt-6" onClick={() => navigate("/dashboard/contacts")}>
          Back to contacts
        </Button>
      </div>
    );
  }
  if (!contact) return <Loading />;

  const ctx = contact.relationship_context || {};

  return (
    <div data-testid="contact-detail-page">
      <Link to="/dashboard/contacts" data-testid="contact-back"
        className="inline-flex items-center gap-1.5 text-sm font-[560] text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to contacts
      </Link>

      <header className="mt-4 flex flex-col gap-4 border-b border-border pb-5 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="truncate text-2xl font-[680] tracking-[-0.01em]"
              data-testid="contact-display-name">{contact.display_name}</h1>
            <button type="button" onClick={() => patch({ favorite: !contact.favorite })}
              aria-pressed={contact.favorite} aria-label="Toggle favorite"
              data-testid="contact-detail-favorite"
              className={`rounded p-1 ${contact.favorite ? "text-amber-500" : "text-muted-foreground/60"}`}>
              <Star className={`h-5 w-5 ${contact.favorite ? "fill-amber-400" : ""}`} />
            </button>
          </div>
          <p className="mt-1 text-muted-foreground" data-testid="contact-subtitle">
            {[contact.orgs?.[0]?.title, contact.orgs?.[0]?.org_name, contact.city]
              .filter(Boolean).join(" · ") || "No organization yet"}
          </p>
          <div className="mt-3">
            <ContactTags contactId={contact.id} tags={contact.tags} onChanged={load} />
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setEdit(true)} data-testid="contact-edit">
            <Pencil className="mr-1.5 h-4 w-4" /> Edit
          </Button>
          <Button variant="outline" size="sm" data-testid="contact-archive"
            onClick={async () => {
              await api.post(`/crm/contacts/${contact.id}/archive`, { archived: !contact.archived });
              toast.success(contact.archived ? "Contact restored to your list" : "Contact archived");
              load();
            }}>
            {contact.archived ? <Undo2 className="mr-1.5 h-4 w-4" /> : <Archive className="mr-1.5 h-4 w-4" />}
            {contact.archived ? "Unarchive" : "Archive"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)}
            data-testid="contact-delete"
            className="text-destructive hover:bg-destructive/10 hover:text-destructive">
            <Trash2 className="mr-1.5 h-4 w-4" /> Delete
          </Button>
        </div>
      </header>

      <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-8">
          <ContactSubrecords contactId={contact.id} title="Contact methods" testid="contact-methods"
            path="methods" rows={contact.methods} onChanged={load} enums={enums}
            describe={(r) => r.value}
            fields={[
              { key: "kind", label: "Type", options: "enum", enumKey: "method_kinds" },
              { key: "value", label: "Value", max: 254 },
              { key: "label", label: "Label", options: "enum", enumKey: "method_labels" },
              { key: "city", label: "City (addresses)", group: "address", max: 80 },
            ]} />

          <ContactSubrecords contactId={contact.id} title="Organizations" testid="contact-orgs"
            path="orgs" rows={contact.orgs} onChanged={load}
            describe={(r) => [r.title, r.org_name].filter(Boolean).join(" · ")}
            fields={[
              { key: "org_name", label: "Organization", max: 150 },
              { key: "title", label: "Title", max: 100 },
              { key: "org_type", label: "Type", max: 60 },
            ]} />

          <ContactSubrecords contactId={contact.id} title="Affiliations" testid="contact-affiliations"
            path="affiliations" rows={contact.affiliations} onChanged={load} enums={enums}
            describe={(r) => [r.name, r.role].filter(Boolean).join(" · ")}
            fields={[
              { key: "name", label: "Name", max: 150 },
              { key: "kind", label: "Kind", options: "enum", enumKey: "affiliation_kinds" },
              { key: "role", label: "Role", max: 100 },
            ]} />

          <ContactNotes contactId={contact.id} notes={contact.notes} onChanged={load} />

          <ContactTimeline contactId={contact.id} />
        </div>

        <aside className="space-y-6">
          <section className="rounded-xl border border-border bg-card p-4"
            data-testid="contact-startup-otter">
            <h2 className="text-base font-[640]">Startup Otter</h2>
            {contact.linked_person ? (
              <div className="mt-3">
                <div className="flex items-center gap-3">
                  {contact.linked_person.headshot_url
                    ? <img src={contact.linked_person.headshot_url} alt=""
                      className="h-10 w-10 rounded-full object-cover" />
                    : <span className="h-10 w-10 rounded-full bg-secondary" />}
                  <div className="min-w-0">
                    <p className="truncate text-sm font-[600]" data-testid="linked-person-name">
                      {contact.linked_person.display_name}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {contact.linked_person.headline}
                    </p>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {actions?.profile_url && (
                    <Link to={actions.profile_url} data-testid="contact-open-profile">
                      <Button size="sm" variant="outline">
                        <ExternalLink className="mr-1.5 h-4 w-4" /> Profile
                      </Button>
                    </Link>
                  )}
                  {actions?.message?.available && (
                    <Link to={actions.message.url} data-testid="contact-message">
                      <Button size="sm" variant="outline">
                        <MessageSquare className="mr-1.5 h-4 w-4" /> Message
                      </Button>
                    </Link>
                  )}
                  {actions?.email && (
                    <a href={`mailto:${actions.email}`} data-testid="contact-email-action">
                      <Button size="sm" variant="outline">
                        <Mail className="mr-1.5 h-4 w-4" /> Email
                      </Button>
                    </a>
                  )}
                  <UnlinkButton contactId={contact.id} onUnlinked={load} />
                </div>
                {actions && !actions.message?.available && (
                  <p className="mt-2 text-xs text-muted-foreground" data-testid="contact-message-blocked">
                    Messaging follows Startup Otter's normal rules — connect first to send a message.
                  </p>
                )}
                <p className="mt-2 text-[11px] text-muted-foreground">
                  This link is private to you. They aren't notified.
                </p>
              </div>
            ) : (
              <div className="mt-3">
                <p className="text-sm text-muted-foreground">
                  Not linked to a Startup Otter member yet.
                </p>
                <Button size="sm" variant="outline" className="mt-3"
                  onClick={() => setLinking(true)} data-testid="contact-link-button">
                  <Link2 className="mr-1.5 h-4 w-4" /> Link to a member
                </Button>
              </div>
            )}
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/crm/contacts/${contact.id}/vcard`}
              data-testid="contact-vcard"
              className="mt-4 inline-block text-xs font-[560] text-primary hover:underline">
              Download vCard
            </a>
          </section>

          <section className="rounded-xl border border-border bg-card p-4" data-testid="contact-relationship">
            <h2 className="text-base font-[640]">Relationship</h2>
            <dl className="mt-3 space-y-3">
              <Field label="How we met" testid="contact-how-we-met-value">{ctx.how_we_met}</Field>
              <Field label="First connected" testid="contact-first-connected">
                {[ctx.first_connected?.date, pretty(ctx.first_connected?.method),
                  ctx.first_connected?.location_text].filter(Boolean).join(" · ")}
              </Field>
              <Field label="Status" testid="contact-status-value">
                {pretty(contact.relationship_status)}
              </Field>
              <Field label="Contact type" testid="contact-types-value">
                {(contact.contact_types || []).map(pretty).join(", ")}
              </Field>
              <Field label="Stay in touch" testid="contact-cadence-value">
                {ctx.cadence_days ? `Every ${ctx.cadence_days} days` : null}
              </Field>
              <Field label="Next touch due" testid="contact-next-touch">
                {contact.next_touch_at ? new Date(contact.next_touch_at).toLocaleDateString() : null}
              </Field>
              <Field label="Relationship goal" testid="contact-goal">{ctx.relationship_goal}</Field>
            </dl>
          </section>

          <section className="rounded-xl border border-border bg-card p-4" data-testid="contact-topics">
            <h2 className="text-base font-[640]">Needs &amp; can help</h2>
            <dl className="mt-3 space-y-3">
              <Field label="Looking for" testid="contact-needs">
                {(contact.needs || []).map((n) => n.label).join(", ")}
              </Field>
              <Field label="Can help with" testid="contact-can-help">
                {(contact.can_help || []).map((n) => n.label).join(", ")}
              </Field>
              <Field label="Interests" testid="contact-interests">
                {(ctx.interests || []).map((n) => n.label).join(", ")}
              </Field>
            </dl>
          </section>
        </aside>
      </div>

      <ContactFormDialog open={edit} onOpenChange={setEdit} enums={enums} contact={contact}
        onSaved={load} />

      <LinkPersonDialog open={linking} onOpenChange={setLinking} contactId={contact.id}
        onLinked={load} />

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="contact-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this contact?</AlertDialogTitle>
            <AlertDialogDescription>
              It leaves your list right away and stays recoverable for 30 days. This never
              affects the person's own Startup Otter account or profile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="contact-delete-cancel">Keep contact</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="contact-delete-confirm">
              Delete contact
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
